import React, { Component } from "react";
import shaka from "shaka-player/dist/shaka-player.ui";

import { getToken } from "../../Helper/Api";

const properties = require("../../properties.json");

// Plain HTML5 <video controls> exposes play/pause/seek/volume, but browsers hide the playback
// speed control behind a right-click menu (Chrome) or don't offer discrete steps at all (some
// browsers). This adds an explicit, always-visible speed row + a fullscreen button on top of the
// native player instead of trying to replace it.
const SPEEDS = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];

// Watermark positions it cycles through -- see WATERMARK_MOVE_MS below for why it moves at all.
const WATERMARK_POSITIONS = [
  { top: "6%", left: "6%" },
  { top: "6%", right: "6%" },
  { bottom: "10%", left: "6%" },
  { bottom: "10%", right: "6%" },
  { top: "45%", left: "42%" },
];
const WATERMARK_MOVE_MS = 8000;

/** Plays a course video. If `hlsManifestUrl` is set (the item finished encrypting -- see
 *  CourseItemResponse.hlsManifestUrl), plays the AES-CTR encrypted HLS stream through shaka-player
 *  with ClearKey DRM, fetching the decryption key from our own license endpoint (auth + enrollment
 *  checked server-side, see ClearKeyLicenseController). Otherwise falls back to the plain
 *  presigned `src` -- e.g. while a just-uploaded video is still being transcoded. Either way the
 *  underlying element is a normal <video>, so the speed-button row and fullscreen button work
 *  unchanged in both modes.
 *
 *  `viewerLabel` (the logged-in viewer's own name, from CourseResponse) is burned into the video
 *  as a moving, low-opacity watermark -- doesn't stop screen-recording, but ties any leaked copy
 *  back to whoever's account played it. It moves periodically so a leaker can't just crop/blur
 *  one fixed spot to remove it. */
class VideoPlayer extends Component {
  videoRef = React.createRef();
  containerRef = React.createRef();
  player = null;
  watermarkTimer = null;
  state = { rate: 1, watermarkIndex: 0 };

  componentDidMount() {
    const { hlsManifestUrl, viewerLabel, onProgress } = this.props;
    if (hlsManifestUrl) {
      this.initShaka(hlsManifestUrl);
    }
    if (viewerLabel) {
      this.watermarkTimer = setInterval(() => {
        this.setState((s) => ({ watermarkIndex: (s.watermarkIndex + 1) % WATERMARK_POSITIONS.length }));
      }, WATERMARK_MOVE_MS);
    }
    // Periodic resume-position reporting (see CourseResponse.lastPositionSeconds) -- polling
    // instead of the video's own `timeupdate` event, which fires many times a second and would
    // be excessive to save on every tick.
    if (onProgress) {
      this.progressTimer = setInterval(() => {
        const v = this.videoRef.current;
        if (v && !v.paused && !v.seeking) {
          onProgress(Math.floor(v.currentTime));
        }
      }, 10000);
    }
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.watermarkTimer) {
      clearInterval(this.watermarkTimer);
    }
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }
    if (this.props.onProgress && this.videoRef.current) {
      this.props.onProgress(Math.floor(this.videoRef.current.currentTime));
    }
  }

  resumeOnce = () => {
    const { resumeSeconds } = this.props;
    if (resumeSeconds && this.videoRef.current && !this.resumed) {
      this.resumed = true;
      this.videoRef.current.currentTime = resumeSeconds;
    }
  };

  initShaka = async (manifestUrl) => {
    shaka.polyfill.installAll();
    if (!shaka.Player.isBrowserSupported()) {
      console.error("shaka-player: browser not supported for encrypted playback");
      return;
    }
    const player = new shaka.Player(this.videoRef.current);
    this.player = player;
    player.addEventListener("error", (e) => console.error("shaka-player error", e.detail));

    const licenseUrl = `${properties.api_path}/course-media/license/clearkey/${this.props.itemId}`;
    player.configure({ drm: { servers: { "org.w3.clearkey": licenseUrl } } });
    player.getNetworkingEngine().registerRequestFilter((type, request) => {
      if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
        // Shaka sends the ClearKey body as raw bytes; the endpoint only accepts JSON.
        request.headers["Content-Type"] = "application/json";
        const token = getToken();
        if (token) {
          request.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    });

    try {
      await player.load(manifestUrl);
      this.setRate(this.state.rate);
    } catch (e) {
      console.error("shaka-player load failed", e);
    }
  };

  setRate = (rate) => {
    if (this.videoRef.current) {
      this.videoRef.current.playbackRate = rate;
    }
    this.setState({ rate });
  };

  goFullscreen = () => {
    // The whole container, not just <video>, so the watermark overlay (a sibling element)
    // keeps showing in fullscreen too -- fullscreen is exactly when screen-recording is likeliest.
    const el = this.containerRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  render() {
    const { src, hlsManifestUrl, viewerLabel } = this.props;
    const position = WATERMARK_POSITIONS[this.state.watermarkIndex];
    return (
      <div className="video-player-frame">
        <div className="video-watermark-wrap" ref={this.containerRef}>
          <video
            ref={this.videoRef}
            src={hlsManifestUrl ? undefined : src}
            controls
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            onContextMenu={(e) => e.preventDefault()}
            autoPlay
            style={{ width: "100%", display: "block" }}
            onLoadedMetadata={() => {
              this.setRate(this.state.rate);
              this.resumeOnce();
            }}
          />
          {viewerLabel && (
            <div className="video-watermark" style={position}>
              {viewerLabel} · {new Date().toLocaleString("en-GB")}
            </div>
          )}
        </div>
        <div className="video-controls-extra">
          <span className="speed-label">Speed</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              className={`speed-btn ${this.state.rate === s ? "active" : ""}`}
              onClick={() => this.setRate(s)}
            >
              {s}x
            </button>
          ))}
          <button type="button" className="fullscreen-btn" onClick={this.goFullscreen}>
            <i className="fas fa-expand"></i> Fullscreen
          </button>
        </div>
      </div>
    );
  }
}

export default VideoPlayer;
